import { escapeHtml } from '../../../../shared/utils/escapeHtml';
import React, { useMemo, useRef } from 'react';
import './FunnelVsCompetitors.scss';
import ReactECharts from 'echarts-for-react';
import { EChartsOption } from 'echarts';
import { useTranslation } from 'react-i18next';
import type { InsightsBrandFunnelsResponse } from '@brand24-original/llm-types';
import { AllBrandsStatsResponse } from '../../../../shared/models/projects/stats/AllBrandsStatsResponse';
import CustomTooltip from '../../../../shared/components/common/tooltip/CustomTooltip';
import EmptyPanel from '../../../../shared/components/empty-panel/EmptyPanel';
import BrandAxisAvatars from '../../../../shared/components/chart/brand-axis-avatars/BrandAxisAvatars';
import { brandColorOf } from '../../../../shared/components/report/brandColors';
import { getChartThemeColors } from '../../../../shared/utils/chartTheme';
import { useTheme } from '../../../../shared/contexts/theme/ThemeContext';
import { percentOf } from '../../helpers/overviewInsightHelpers';
import { getInsightsMetricColors } from '../../helpers/insightsChartColors';
import { useBrandSelection } from '../../hooks/useBrandSelection';
import BrandPicker from '../brand-picker/BrandPicker';
import useIsMobile from '../../../../shared/hooks/global/UseIsMobile';

interface FunnelVsCompetitorsProps {
  data: InsightsBrandFunnelsResponse | null;
  /** SoV per brand (getAllBrandsStats, sovPerPrompt) — source of the visibility rule in the selector. */
  sovBrands: AllBrandsStatsResponse[] | null;
  isLoading: boolean;
  isError?: boolean;
}

// Telefon: grubość poziomego słupka; z niej (3 słupki + przerwa na markę + legenda) liczona jest wysokość.
const FUNNEL_BAR_MOBILE = 12;

const FunnelVsCompetitors: React.FC<FunnelVsCompetitorsProps> = ({
  data,
  sovBrands,
  isLoading,
  isError,
}) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isMobile = useIsMobile();
  const { focusBrandKey, pickerBrands, selectedKeys, selectedBrands, brandColors, selectBrands } =
    useBrandSelection(data, sovBrands);
  const chartRef = useRef<ReactECharts>(null);

  const total = data?.totalResponses ?? 0;

  // Brand badges over the x-axis as on the report's ranking chart — same BrandIcon and the same brand
  // color map as the picker rows and the matrix headers. Bars stay in the funnel (metric) ladder.
  const avatarItems = useMemo(
    () =>
      selectedBrands.map((brand, index) => ({
        name: brand.label,
        color: brandColorOf(brandColors, brand.label, brand.brandKey === focusBrandKey, index),
      })),
    [selectedBrands, brandColors, focusBrandKey]
  );

  // Funnel ladder: literal copies of the primary-ux-200/300/500 tokens that the
  // "From brand visibility to Top 1" bars use directly in SCSS (canvas can't read CSS vars). Theme is
  // passed explicitly so useMemo has a real dependency.
  const colors = useMemo(() => getInsightsMetricColors(theme), [theme]);

  const chartOptions: EChartsOption = useMemo(() => {
    const chartColors = getChartThemeColors();
    const seriesDefs = [
      {
        name: t('insightPage.funnelVsCompetitors.legendVisibility'),
        key: 'visible' as const,
        color: colors.funnelVisibility,
      },
      {
        name: t('insightPage.funnelVsCompetitors.legendTop3'),
        key: 'top3' as const,
        color: colors.funnelTop3,
      },
      {
        name: t('insightPage.funnelVsCompetitors.legendTop1'),
        key: 'top1' as const,
        color: colors.funnelTop1,
      },
    ];

    const legend = {
      bottom: 0,
      icon: 'circle',
      itemWidth: 10,
      itemHeight: 10,
      itemGap: 20,
      textStyle: { fontSize: 12, color: chartColors.textBlack },
    };
    const tooltip = {
      trigger: 'axis' as const,
      axisPointer: { type: 'shadow' as const },
      borderRadius: 8,
      padding: [5, 10],
      borderColor: chartColors.tooltipBorder,
      backgroundColor: chartColors.tooltipBg,
      textStyle: { color: chartColors.tooltipText, fontSize: 12, lineHeight: 22 },
      formatter: (params: any) => {
        const list = Array.isArray(params) ? params : [params];
        const header = list[0]?.axisValue ?? '';
        const rows = list.map(
          (param) => `
            <div class="chart-tooltip__row">
              <div class="chart-tooltip__marker">${param.marker}</div>
              <div class="chart-tooltip__text"><span>${escapeHtml(param.seriesName)}</span></div>
              <div><strong>${param.value}%</strong></div>
            </div>`
        );
        return `<div class="chart-tooltip__content"><strong>${escapeHtml(header)}</strong>${rows.join('')}</div>`;
      },
    };

    // Telefon: ten sam wykres obrócony — marki jedna pod drugą (nazwa przy osi), trzy poziome słupki
    // z wartością na końcu — zamiast kolumn, których nazwy i ikony nachodziły na siebie.
    if (isMobile) {
      return {
        grid: { top: 0, left: 0, right: 40, bottom: 40, containLabel: true },
        yAxis: {
          type: 'category',
          inverse: true,
          data: selectedBrands.map((brand) => brand.label),
          axisTick: { show: false },
          axisLine: { show: false },
          axisLabel: {
            color: chartColors.axisLabel,
            fontSize: 12,
            width: 96,
            overflow: 'truncate',
          },
        },
        xAxis: {
          type: 'value',
          max: 100,
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: { show: false },
          splitLine: { show: false },
        },
        legend,
        tooltip,
        series: seriesDefs.map((def) => ({
          name: def.name,
          type: 'bar' as const,
          data: selectedBrands.map((brand) => percentOf(brand[def.key], total)),
          barGap: '20%',
          barWidth: FUNNEL_BAR_MOBILE,
          itemStyle: { color: def.color, borderRadius: [0, 4, 4, 0] },
          label: {
            show: true,
            position: 'right' as const,
            formatter: '{c}%',
            fontSize: 10,
            color: chartColors.axisLabel,
          },
        })),
      };
    }

    return {
      grid: { top: 16, left: 8, right: 8, bottom: 56, containLabel: true },
      // No axis lines, horizontal splitLine only (as in the shared BarChart). X labels as in
      // BrandVisibilityBarChart: 11px truncated names pushed down by `margin` to leave room for the
      // BrandAxisAvatars badge above each name (containLabel accounts for the margin).
      xAxis: {
        type: 'category',
        data: selectedBrands.map((brand) => brand.label),
        axisTick: { show: false },
        axisLine: { show: false },
        axisLabel: {
          color: chartColors.axisLabel,
          interval: 0,
          margin: 42,
          fontSize: 11,
          width: 74,
          overflow: 'truncate',
        },
      },
      yAxis: {
        type: 'value',
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { color: chartColors.axisLabel, formatter: '{value}%' },
        splitLine: { lineStyle: { color: chartColors.splitLine } },
      },
      // Legend text in --text-black like the overview charts' HTML legends (canvas → literal from chartTheme).
      legend,
      tooltip,
      series: seriesDefs.map((def) => ({
        name: def.name,
        type: 'bar' as const,
        data: selectedBrands.map((brand) => percentOf(brand[def.key], total)),
        barGap: '10%',
        barMaxWidth: 24,
        itemStyle: { color: def.color, borderRadius: [4, 4, 0, 0] },
      })),
    };
    // `colors` is memoized on the theme, so a theme switch also re-runs getChartThemeColors() here.
  }, [selectedBrands, total, colors, t, isMobile]);

  const hasData = !!data && total > 0 && data.brands.length > 0;

  return (
    <div className="stats__panel funnel-vs-competitors">
      <div className="page-box__title-action">
        <div className="page-box__title page-box__title--flex">
          {t('insightPage.funnelVsCompetitors.title')}
          <CustomTooltip
            tooltipText="insightPage.funnelVsCompetitors.tooltip"
            addMargin={false}
          />
        </div>
        {!isLoading && !isError && hasData && (
          <BrandPicker
            brands={pickerBrands}
            focusBrandKey={focusBrandKey}
            selectedKeys={selectedKeys}
            brandColors={brandColors}
            onChange={selectBrands}
          />
        )}
      </div>
      <div className="funnel-vs-competitors__card">
        {isLoading ? (
          <div className="funnel-vs-competitors__skeleton" />
        ) : isError ? (
          <EmptyPanel textKey="somethingWentWrong" />
        ) : !hasData ? (
          <EmptyPanel />
        ) : (
          <div
            className="funnel-vs-competitors__chart"
            style={
              isMobile
                ? { height: `${selectedBrands.length * (3 * (FUNNEL_BAR_MOBILE + 3) + 24) + 40}px` }
                : undefined
            }
          >
            {/* Keyed together: a theme switch remounts the chart, the overlay must re-find the new DOM.
                Ikony marek nad osią X — tylko w układzie pionowym (na telefonie nazwy stoją przy osi Y). */}
            <React.Fragment key={`${theme}-${isMobile}`}>
              <ReactECharts
                ref={chartRef}
                option={chartOptions}
                notMerge
                style={{ height: '100%', width: '100%' }}
              />
              {!isMobile && <BrandAxisAvatars chartRef={chartRef} items={avatarItems} size="md" />}
            </React.Fragment>
          </div>
        )}
      </div>
    </div>
  );
};

export default FunnelVsCompetitors;
